import React from 'react';
import { GetServerSideProps } from 'next';
import { useRouter } from 'next/router';
import { useSelector } from 'react-redux';
import _ from 'lodash';

import reduxWrapper from '@src/store';
import {
	getCategoriesState,
	getSearchState,
	getCategories,
} from '@src/store/selectors/category';
import {
	getAllCategoriesCall,
	getCommoditySearchCall,
	getCommodityPublishedTypeCall,
} from '@src/store/actions/category';
import { sendBugMsgCall } from '@src/store/actions/session';
import { setSSRPage } from '@src/store/actions/ssr';
import { defaultActionCalls } from '@src/router/ssr';
import { SSR_PAGE_CATEGORY } from '@src/router/hydrate';
import { getApiData, getPageTimeoutData } from '@src/utils/deprecatedCommon';
import { STATE_FAIL, STATE_SUCCESS } from '@src/const/common';
import useCheckCloseWebPrivatePage from '@src/hooks/useCheckCloseWebPrivatePage';

import CategoryPage from '@src/components/category';
import CustomSeo from '@src/components/seo/customSeo';
import RedirectClosedLogin from '@src/pages/redirectClosedLogin';

const Category = (): JSX.Element => {
	const router = useRouter();
	const { shortLink } = router.query;
	const categories = useSelector(getCategories);
	const category = _.find(categories, { short_link: shortLink });
	const seoSetting = {
		seoTitle: category?.seo_title || '',
		seoDescription: category?.seo_description || '',
		seoKeywords: category?.seo_keywords || '',
	};
	const needLogin = useCheckCloseWebPrivatePage();
	return (
		<>
			<RedirectClosedLogin needLogin={needLogin} />
			<CustomSeo seoSetting={seoSetting} />
			<CategoryPage />
		</>
	);
};

export const getServerSideProps: GetServerSideProps = reduxWrapper.getServerSideProps(
	async ctx => {
		const { req, store, query } = ctx;
		const { ssrHeaders } = getApiData(req);
		let { shortLink } = query;
		const { commodity_type, tag_ids, teacher_id, category_short_link, page, page_size } = query;
		const time = 3000;
		const pageTimeout = setTimeout(() => {
			const pageTimeoutData = getPageTimeoutData({ req, time });

			if (pageTimeoutData) {
				store.dispatch(sendBugMsgCall({ msg: JSON.stringify(pageTimeoutData) }));
			}
		}, time);

		store.dispatch(setSSRPage(SSR_PAGE_CATEGORY));

		const success = await new Promise<() => void>(resolve => {
			const unsubscribe = store.subscribe(() => {
				const state = store.getState();

				const defaultActionDone = defaultActionCalls.actionDone(ctx);
				const categoriesState = getCategoriesState(state);
				const searchState = getSearchState(state);

				if (defaultActionDone && (categoriesState === STATE_FAIL)) {
					// eslint-disable-next-line no-param-reassign
					ctx.res.statusCode = 404;
					clearTimeout(pageTimeout);
					resolve(unsubscribe);

					return;
				}
				const isSuccess = categoriesState === STATE_SUCCESS && searchState === STATE_SUCCESS;
				if (defaultActionDone && isSuccess) {
					clearTimeout(pageTimeout);
					resolve(unsubscribe);
				}
			});

			defaultActionCalls.actionCall(ctx);
			store.dispatch(getCommodityPublishedTypeCall({ ssrHeaders }));
			store.dispatch(getAllCategoriesCall({ ssrHeaders }));
			if (shortLink === 'all') shortLink = '';
			store.dispatch(getCommoditySearchCall({
				ssrHeaders,
				// category_id: categoryId,
				commodity_type,
				tag_ids,
				teacher_id,
				category_short_link: category_short_link || shortLink,
				page: page || 0,
				page_size: page_size || 15,
			}));
		});

		success();
	},
);

export default Category;
